/**
 * CR456: Hook for FLYER sources view + mapping updates
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { FlyerSourceRow } from "@/lib/imageCapture/validateMapping";

export function useFlyerSources() {
  const { toast } = useToast();
  const [sources, setSources] = useState<FlyerSourceRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSources = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("v_flyer_image_capture_sources")
      .select("*")
      .order("merchant_id");
    if (error) {
      toast({ title: "Error loading flyer sources", description: error.message, variant: "destructive" });
    } else {
      setSources((data ?? []) as FlyerSourceRow[]);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const updateMapping = async (
    sourceId: string,
    payload: {
      flyer_image_capture_enabled?: boolean;
      flyer_image_capture_adapter_key?: string | null;
      flyer_image_capture_params?: unknown | null;
    }
  ) => {
    const { error } = await supabase
      .from("merchant_sources")
      .update({ ...payload, updated_at: new Date().toISOString() } as any)
      .eq("id", sourceId);
    if (error) {
      toast({ title: "Error saving mapping", description: error.message, variant: "destructive" });
      return false;
    }
    toast({ title: "Mapping saved" });
    await fetchSources();
    return true;
  };

  return { sources, loading, fetchSources, updateMapping };
}
